import { studyQueryKeys } from '@/config/queryKeys';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { PaginatedSampleProteoformsResponse, SampleProteoformPublic } from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { NUMBER_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';

interface TauSamplesDetailTableProps {
  orgId: string;
  studyId: string;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-tau-detail-pageSize`;
const VALID_SORT_FIELDS = ['sample_name', 'instrument_run_name', 'proteoform_name', 'proteoform_abundance'];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

// PTM/isoform columns: visible per wireframe; data will be populated once
// SampleProteoformPublic exposes these fields from the backend.
const TAU_STUB_COLUMNS: ColDef<SampleProteoformPublic>[] = [
  'N',
  '0N',
  '1N',
  '2N',
  '3R',
  '4R',
  'C',
  'pT181',
  'pS202+PT205',
  'pT205',
  'pS214',
  'p217',
  'pT231',
  'pS396',
].map(name => ({
  colId: name,
  headerName: name,
  resizable: true,
  sortable: false,
  filter: false,
}));

export const TauSamplesDetailTable = function TauSamplesDetailTable({ orgId, studyId }: TauSamplesDetailTableProps) {
  const { t } = useTranslation('studies');
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<SampleProteoformPublic, PaginatedSampleProteoformsResponse>(
        studyQueryKeys.endpoints.proteoformsSearch(orgId, studyId),
        ({ limit, skip, sortBy, sortOrder, filters }) => {
          // TODO: add assay_version filter to SampleProteoformFilters when backend supports it
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';
          return {
            skip,
            limit,
            sort_by: validSortBy as 'sample_name' | 'instrument_run_name' | 'proteoform_name' | 'proteoform_abundance',
            sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
            filters: Object.keys(filters).length > 0 ? filters : null,
          };
        },
      ),
    [orgId, studyId],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<SampleProteoformPublic>({
    studyId,
    tableId: 'tau-samples-detail-table',
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      {
        field: 'sample_name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_name',
        headerName: t('columnHeaders.proteoformGroup'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_abundance',
        headerName: t('columnHeaders.abundance'),
        filter: 'agNumberColumnFilter',
        filterParams: NUMBER_FILTER_PARAMS,
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => value?.toLocaleString() ?? '',
      },
      ...TAU_STUB_COLUMNS,
    ],
    [t],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  return (
    <Table
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      enableResponsiveResize={false}
      getRowId={(params: { data: SampleProteoformPublic }) => `${params.data.sample_id}-${params.data.proteoform_name}`}
      gridOptions={gridOptions}
      onFilterChanged={handleFilterChanged}
      onFirstDataRendered={handleFirstDataRendered}
      onGridPreDestroyed={e => flushColumnState(e.api, 'tau-samples-detail-table')}
      onGridReady={handleGridReady}
      onModelUpdated={handleModelUpdated}
      onPaginationChanged={handlePaginationChanged}
      pagination
      paginationPageSize={pageSize}
      paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
      ref={gridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId="tau-samples-detail-table"
    />
  );
};
